import { Formik, Form, useFormikContext } from 'formik'
import { isEmpty, toNumber } from 'lodash'
import throttle from 'lodash/throttle'
import { prop, evolve, mapObjIndexed, pipe, propEq, find, isNil } from 'ramda'
import { useState, useEffect, useCallback } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation, Trans } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router'
import { Route, Switch } from 'react-router-dom'
import { buildYup } from 'schema-to-yup'
import styled from 'styled-components'
import { Button } from '@/components/button'
import { DefaultButtonBar } from '@/components/button-bar'
import LoadingIndicator from '@/components/loading-indicator'
import showMessageModal from '@/components/message-modal'
import Page from '@/components/page'
import PageTitle from '@/components/page-title'
import DashboardHeader from '@/features/Listing/dashboard-header'
import useListingAct from '@/hooks/use-listing-act'
import useMediaQuery from '@/hooks/use-media-query'
import useProfile from '@/hooks/use-profile'
import { select } from '@/store'
import { breakpoints, colors, fontWeights, structure } from '@/styles'
import { defaultIfFalsy } from '@/utils'
import flattenObj from '@/utils/flatten-obj'
import { toUnit } from '@/utils/styles'
import { Box } from '@/components/structure'
import { useToaster } from '@/models/toaster/hooks'
import { isNullOrEmpty } from '@/utils'
import backgroundTopRight from './images/background-top-right.png'
import backgroundTopLeft from './images/background-top-left.png'
import backgroundBottom from './images/background-bottom.png'
import ContractTerms from './ContractTerms'
import ContractParties from './ContractParties'
import { TabResolver, NavigationTabs } from './ContractNavigation'
import ContractAddendums from './ContractAddendums'
// import ContractDocuments from './ContractDocuments'

const StyledPage = styled(Page)`
  position: relative;
`

const PageHeader = styled.div`
  top: ${toUnit(structure.header.height)};
  width: 100%;
  background-color: ${colors.white};

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    top: ${structure.header.heightMobile}px;
  }
`

const PageHeaderOutter = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-start;
`

const PageHeaderInner = styled.div`
  max-width: 700px;
  margin: 0 100px;
  padding: 30px 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;

  @media only screen and (max-width: ${breakpoints.desktop}) {
    margin: 0 50px;
  }

  @media only screen and (max-width: ${breakpoints.tabletLandscape}) {
    margin: 0 25px;
    padding: 10px 0;
  }

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    flex-direction: column-reverse;
    justify-content: center;
    padding: 30px 0 10px;
  }
`

const LeftCloud = styled.img`
  pointer-events: none;
  position: absolute;
  width: 185px;
  top: 140px;
  left: -170px;

  @media only screen and (max-width: ${breakpoints.tabletLandscape}) {
    display: none;
  }
`

const RightCloud = styled.img`
  pointer-events: none;
  position: absolute;
  z-index: 2;
  width: 190px;
  top: 100px;
  right: 0;

  @media only screen and (max-width: ${breakpoints.tabletLandscape}) {
    width: 150px;
    top: 80px;
  }

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    object-position: 10px 0;
    width: 100px;
    top: 155px;
  }
`

const HeaderTitle = styled.h1`
  font-size: 30px;
  color: ${colors.regular};
  font-weight: ${fontWeights.bold};
  line-height: 41px;
  margin: 0 0 50px;

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    margin: 50px 0 0;
  }
`

const Content = styled.div`
  display: flex;
  flex: 1;
  background: ${colors.white};

  background-image: url(${backgroundBottom});
  background-repeat: no-repeat;
  background-position: 100% calc(100% - 100px);
  background-size: 400px;

  @media screen and (max-width: ${breakpoints.desktop}) {
    background-size: 280px;
  }

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    background-position: 100% calc(100% - 60px);
    background-size: 240px;
  }
`

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
`

const ContentInner = styled.div`
  max-width: 700px;
  margin: 0 100px;
  padding-top: 20px;
  display: flex;
  flex: 1;
  flex-direction: column;

  @media only screen and (max-width: ${breakpoints.desktop}) {
    margin: 0 50px;
  }

  @media only screen and (max-width: ${breakpoints.tabletLandscape}) {
    margin: 0 25px 50px;
  }
`

const StyledLoadingIndicator = styled(LoadingIndicator)`
  position: absolute;
  top: -30px;
  left: 0px;
  z-index: 11;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px 0;
`

const getInitialValues = (contract, act, listing, applicant) =>
  pipe(
    mapObjIndexed(defaultIfFalsy('')),
    evolve({
      contract_features: defaultIfFalsy([]),
      contract_custom_features: defaultIfFalsy([]),
      contract_utilities: defaultIfFalsy([]),
      contract_custom_utilities: defaultIfFalsy([]),
      custom_field_types: defaultIfFalsy({}),
      parking: defaultIfFalsy({
        enabled: false,
        name: '',
        hasFee: false,
        fee_txt_id: 'parking',
        fee_frequency_txt_id: '',
        fee: ''
      }),
      storage: defaultIfFalsy({
        enabled: false,
        name: '',
        hasFee: false,
        fee_txt_id: 'storage',
        fee_frequency_txt_id: '',
        fee: ''
      }),
      contract_fees: defaultIfFalsy([]),
      additional_fees: defaultIfFalsy([]),
      contract_landlords: defaultIfFalsy([]),
      contract_signing_tenants: tenants => {
        if (!isEmpty(tenants)) return tenants
        if (!isNil(applicant)) return [applicant]
        return []
      },
      contract_addendums: defaultIfFalsy([]),
      contract_extra_users: defaultIfFalsy([]),
      security_deposit: securityDeposit => {
        if (!act?.form_validation?.properties?.security_deposit) return null
        if (securityDeposit) return securityDeposit
        const daysByFrequency = { daily: 1, weekly: 7, monthly: 30 }
        const price = Number(prop('price', listing))
        const frequency = prop('price_frequency', listing)
        const divisor = daysByFrequency[frequency]
        if (isNaN(price) || !divisor) return null
        const isBC = act?.state === 'BC'
        const fullDeposit = (price / divisor) * 30
        // Prefill security deposit field - 1/2 month for BC, 1 month for ON, AB
        const deposit = isBC ? fullDeposit / 2 : fullDeposit
        return Number(deposit).toFixed(2)
      }
    })
  )(contract)

const CreateContractForm = ({ contract, listingId, applicationId, contractId }) => {
  const history = useHistory()
  const { pathname, state } = useLocation()
  const { t } = useTranslation()
  const {
    data: act = {},
    isLoading,
    isError
  } = useListingAct(listingId, {
    retry: 2
  })
  const dispatch = useDispatch()
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.phoneMax})`)
  const [errorModalDisplayed, setErrorModalDisplayed] = useState(false)
  const [validPartiesInfo, setValidPartiesInfo] = useState(true)
  const [validTermsInfo, setValidTermsInfo] = useState(true)

  const [currentStep, setCurrentStep] = useState('parties')

  const listing = useSelector(state => select.listing.listingDetail(state, { listingId }))
  const allApplicants = useSelector(select.listing.applicants)
  const applicant = find(propEq('id', state?.applicantId))(allApplicants)
  const initialValues = getInitialValues(contract, act, listing?.listings, applicant)

  const handleBack = () => {
    switch (currentStep) {
      case 'parties':
        history.goBack()
        break
      case 'terms':
        history.replace(getUrl())
        break
      case 'addendums':
        history.replace(getUrl('terms'))
        break
      default:
        history.goBack()
        break
    }
  }

  useEffect(() => {
    if (pathname.includes('terms')) {
      return setCurrentStep('terms')
    } else if (pathname.includes('addendums')) {
      return setCurrentStep('addendums')
    } else {
      return setCurrentStep('parties')
    }
  }, [pathname])

  const FormikPatchTouched = () => {
    // https://github.com/formium/formik/issues/445
    const { errors, setFieldTouched, isSubmitting, isValidating } = useFormikContext()
    useEffect(() => {
      if (isSubmitting && !isValidating) {
        for (const path of Object.keys(flattenObj(errors))) {
          setFieldTouched(path, true, false)
        }
      }
    }, [errors, isSubmitting, isValidating, setFieldTouched])
    return null
  }

  const getUrl = useCallback(
    (appendPath = '') => {
      const part = applicationId ? `/application/${applicationId}` : `/listing/${listingId}`
      return `${part}/contract/create/${contractId ? `${contractId}/` : ''}${appendPath}`
    },
    [applicationId, contractId, listingId]
  )

  const getPath = (appendPath = '') => {
    const part = applicationId ? '/application/:applicationId' : '/listing/:listingId'
    return `${part}/contract/create/${contractId ? ':contractId/' : ''}${appendPath}`
  }

  const handleSave = async values => {
    const action = contractId ? dispatch.contract.save : dispatch.contract.create
    const { error } = await action({
      contractId,
      listing_id: toNumber(listingId),
      ...values
    })
    if (!error) {
      history.replace(`/listing/${listingId}`)
    }
  }

  const onNext = async (values, { setSubmitting }) => {
    if (currentStep === 'parties') {
      return history.push(getUrl('terms'))
    } else if (currentStep === 'terms') {
      return history.push(getUrl('addendums'))
    }
    setSubmitting(false)
  }

  const onSubmit = async (values, { setSubmitting }) => {
    const action = contractId ? dispatch.contract.save : dispatch.contract.create
    const { error, body } = await action({
      contractId,
      listing_id: toNumber(listingId),
      ...values
    })
    setSubmitting(false)
    if (!error) {
      history.replace(`/listing/${listingId}/contract/${contractId || body.id}/preview`)
    }
  }

  const showErrorModal = useCallback(
    () =>
      showMessageModal({
        icon: 'warning',
        title: t('l.contract.create.act_not_yet_supported.title', 'Unsupported Location'),
        message: (
          <Trans
            i18nKey="l.contract.create.act_not_yet_supported.error"
            values={{
              location: `${listing?.building?.city}, ${listing?.building?.state}`
            }}
            defaults="We don’t yet support contracts in {{ location }}. Please check back soon, or if you believe this is in error, please reach out to Liv Support."
          />
        ),
        onClose: () => {
          history.goBack()
        }
      }),
    [history, listing, t]
  )

  useEffect(() => {
    if (isError && !errorModalDisplayed) {
      setErrorModalDisplayed(true)
      showErrorModal()
    }
  }, [isError, errorModalDisplayed, showErrorModal])

  if (isLoading) return <StyledLoadingIndicator />

  if (isEmpty(act)) return null

  const partiesSchema = {
    type: 'object',
    properties: {
      contract_extra_users: {
        type: 'array',
        required: false
      },
      contract_non_signing_tenants: {
        type: 'array',
        required: false
      },
      contract_signing_tenants: {
        type: 'array',
        required: true,
        minItems: 1
      },
      contract_landlords: {
        type: 'array',
        required: true,
        minItems: 1
      }
    }
  }
  const partiesConfig = {
    errMessages: {
      contract_signing_tenants: {
        required: t(
          'l.contract.create.signing_tenant_required.error',
          'Must add at least one signing tenant'
        )
      },
      contract_landlords: {
        required: t(
          'l.contract.create.landlord_required.error',
          'Must select at least one landlord'
        )
      }
    }
  }

  const termsSchema = act.form_validation
  const termsConfig = {
    errMessages: {
      $matches: t('b.profile.name.special_characters.error', 'Cannot include special characters'),
      $required: t('common.required', 'Required')
    }
  }

  const termsValidationSchema = buildYup(termsSchema, termsConfig)
  const partiesValidationSchema = buildYup(partiesSchema, partiesConfig)

  const validationSchema = partiesValidationSchema.concat(termsValidationSchema)

  const validate = throttle(data => {
    /* eslint-disable promise/catch-or-return */
    partiesValidationSchema.isValid(data).then(setValidPartiesInfo)
    termsValidationSchema.isValid(data).then(setValidTermsInfo)
    /* eslint-enable promise/catch-or-return */
  }, 2000)

  const submitButtonText =
    currentStep === 'addendums'
      ? t('l.contract.create.preview.button', 'Preview')
      : t('common.next', 'Next')

  const disableButtonWithValidation = currentStep === 'parties'
  return (
    <Formik
      enableReinitialize
      validationSchema={currentStep === 'parties' ? partiesValidationSchema : validationSchema}
      initialValues={initialValues}
      onSubmit={currentStep === 'addendums' ? onSubmit : onNext}
      validateOnMount
    >
      {({ values, isSubmitting, isValid, handleSubmit, validateForm }) => {
        validate(values)
        return (
          <TabResolver getUrl={getUrl}>
            <PageHeader>
              {!displayMobile && (
                <DashboardHeader
                  listingId={listingId}
                  currentPage={t('l.contract.edit.contract.title', 'Contract')}
                  height={55}
                />
              )}
              <RightCloud src={backgroundTopRight} alt="" />
              <PageHeaderOutter>
                <PageHeaderInner>
                  <LeftCloud src={backgroundTopLeft} alt="" />
                  <HeaderTitle>
                    {t('b.contract.view.tenancy_agreement.title', 'Tenancy Agreement')}
                  </HeaderTitle>
                  <NavigationTabs
                    getUrl={getUrl}
                    validPartiesInfo={validPartiesInfo}
                    validTermsInfo={validTermsInfo}
                  />
                </PageHeaderInner>
              </PageHeaderOutter>
            </PageHeader>
            <Content>
              <StyledForm>
                <FormikPatchTouched />
                <ContentInner>
                  <Switch>
                    <Route
                      exact
                      path={getPath()}
                      render={props => (
                        <ContractParties {...props} contractId={contractId} listingId={listingId} />
                      )}
                    />
                    <Route
                      exact
                      path={getPath('terms')}
                      render={props => (
                        <ContractTerms
                          {...props}
                          contractId={contractId}
                          listingId={listingId}
                          act={act}
                          listing={listing}
                        />
                      )}
                    />
                    <Route
                      exact
                      path={getPath('addendums')}
                      render={props => (
                        <FormWrapper>
                          <ContractAddendums {...props} listing={listing} />
                          {/* <ContractDocuments {...props} /> */}
                        </FormWrapper>
                      )}
                    />
                  </Switch>
                </ContentInner>
                <DefaultButtonBar handleBack={handleBack} isSubmitting={isSubmitting}>
                  <Button
                    theme="text"
                    size="small"
                    left={10}
                    disabled={isSubmitting}
                    onClick={() => handleSave(values)}
                  >
                    {t('l.button_bar.edit.save_and_exit.button', 'Save & Exit')}
                  </Button>
                  <Box left="auto">
                    <Button
                      theme="primary"
                      size="small"
                      onClick={() => validateForm().then(() => handleSubmit())}
                      disabled={
                        disableButtonWithValidation &&
                        (!isValid ||
                          isNullOrEmpty(values.contract_landlords) ||
                          isNullOrEmpty(values.contract_signing_tenants))
                      }
                    >
                      {submitButtonText}
                    </Button>
                  </Box>
                </DefaultButtonBar>
              </StyledForm>
            </Content>
          </TabResolver>
        )
      }}
    </Formik>
  )
}

const curr = 'complete_contract.create_contract'

const CreateContract = ({ contract, contractId, listingId, applicationId, noTitle }) => {
  const history = useHistory()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  useEffect(() => {
    dispatch.listing.getApplications(listingId)
  }, [dispatch.listing, listingId])

  const toaster = useToaster()
  const profile = useProfile()
  const isEssentials = profile?.account_type === 'personal'
  const next = !isEssentials ? 'add_addendum' : 'send_contract'
  const state = toaster?.tasks?.complete_contract?.steps?.create_contract?.state
  const updateState = toaster.updateState
  useEffect(() => {
    if (state === 'complete') return
    updateState(curr, {
      step: {
        state: 'complete'
      },
      next: [
        {
          key: next,
          step: { state: 'incomplete' }
        }
      ]
    })
  }, [state, updateState, next])

  return (
    <StyledPage>
      <Helmet>
        <title>{t('b.contract.view.tenancy_agreement.title', 'Tenancy Agreement')}</title>
      </Helmet>
      {!noTitle && (
        <PageTitle>{t('b.contract.view.tenancy_agreement.title', 'Tenancy Agreement')}</PageTitle>
      )}
      <CreateContractForm
        contract={contract}
        listingId={listingId}
        applicationId={applicationId}
        contractId={contractId}
        history={history}
      />
    </StyledPage>
  )
}

export default CreateContract
